import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';

const AlumniPage = () => {
  const [alumni, setAlumni] = useState([]);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    fetchAlumni();
  }, []);

  const fetchAlumni = async () => {
    setLoading(true);
    try {
      const response = await api.get('/alumni');
      setAlumni(response.data);
    } catch (error) {
      console.error('Error fetching alumni:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCardClick = (id) => {
    navigate(`/alumni/${id}`);
  };

  const getRoleBadgeClass = (role) => {
    switch(role) {
      case 'Chairman':
        return 'bg-yellow-100 text-yellow-800';
      case 'Vice Chairman':
        return 'bg-orange-100 text-orange-800';
      case 'Secretary':
        return 'bg-blue-100 text-blue-800';
      case 'Treasurer':
        return 'bg-green-100 text-green-800';
      case 'Patron':
        return 'bg-purple-100 text-purple-800';
      case 'Advisor':
        return 'bg-indigo-100 text-indigo-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getImageUrl = (photoUrl) => {
    if (!photoUrl) return null;
    if (photoUrl.startsWith('http')) return photoUrl;
    if (photoUrl.startsWith('/uploads')) {
      return `${photoUrl}`;
    }
    return `/uploads/alumni/${photoUrl}`;
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div style={{ backgroundColor: '#FEFBEC', minHeight: '100vh' }}>
      {/* Hero Section - Left Aligned with Gold Bottom Border - Reduced Height */}
      <div className="bg-white border-b-4 border-[#fbbf24] shadow-sm">
        <div className="container mx-auto px-4 sm:px-6 py-3 sm:py-4">
          <div className="flex items-center gap-3 sm:gap-4">
            <div className="w-8 h-8 sm:w-10 sm:h-10 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0">
              <span className="text-lg sm:text-xl">👥</span>
            </div>
            <div>
              <h1 className="text-lg sm:text-xl font-bold text-gray-800 mb-0.5">
                Katine TTI Alumni Association
              </h1>
              <p className="text-xs sm:text-sm text-gray-600">
                Meet our distinguished alumni who are making a difference
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 sm:px-6 py-6 sm:py-8">
        {alumni.length === 0 ? (
          <div className="bg-white rounded-lg shadow p-8 text-center">
            <div className="text-6xl mb-4">👥</div>
            <h3 className="text-lg font-semibold text-gray-800 mb-2">No Alumni Found</h3>
            <p className="text-gray-600">Check back later to meet our distinguished alumni.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {alumni.map((alumnus) => {
              const imageUrl = getImageUrl(alumnus.photo_url);

              return (
                <div
                  key={alumnus.id}
                  onClick={() => handleCardClick(alumnus.id)}
                  className="cursor-pointer transform transition-all duration-300 hover:-translate-y-2"
                >
                  <div className="bg-white rounded-lg shadow-md hover:shadow-xl transition-shadow h-full overflow-hidden flex flex-col">
                    {/* Image Section */}
                    <div className="w-full h-64 sm:h-72 overflow-hidden bg-gray-100 flex-shrink-0">
                      {imageUrl ? (
                        <img
                          src={imageUrl}
                          alt={alumnus.name}
                          className="w-full h-full object-cover object-center transition-transform duration-300 hover:scale-110"
                          loading="lazy"
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(alumnus.name)}&background=1e3a8a&color=fff&size=200`;
                          }}
                        />
                      ) : (
                        <div className="w-full h-full bg-gradient-to-br from-primary to-secondary flex items-center justify-center">
                          <span className="text-white text-5xl font-bold">
                            {alumnus.name?.charAt(0)}
                          </span>
                        </div>
                      )}
                    </div>

                    {/* Details Section */}
                    <div className="p-4 text-left flex-grow">
                      <div className="flex items-center justify-between gap-2 mb-3">
                        <h3 className="font-semibold text-gray-800 text-base truncate">
                          {alumnus.name}
                        </h3>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium whitespace-nowrap ${getRoleBadgeClass(alumnus.role)}`}>
                          {alumnus.role}
                        </span>
                      </div>

                      {alumnus.department_name && (
                        <p className="text-sm text-gray-500 mb-2 truncate">{alumnus.department_name}</p>
                      )}

                      {alumnus.graduation_year && (
                        <p className="text-xs text-gray-400 mb-2">Class of {alumnus.graduation_year}</p>
                      )}

                      <div className="mt-3 text-primary text-sm font-medium">
                        View Profile →
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default AlumniPage;
